import { z } from "zod";
import { CURRENCIES } from "./money.js";

/**
 * Le cose che succedono in un gruppo, in ordine di quando sono successe.
 *
 * Il feed **non** si ricava dalle spese e dai pareggi esistenti, anche se a
 * prima vista basterebbe leggerli in ordine di data. Non basta per tre motivi
 * concreti:
 *
 * - una spesa modificata non ricorda com'era prima, quindi «Sara ha modificato
 *   Cena da Nino» sarebbe indistinguibile da «Sara ha aggiunto Cena da Nino»;
 * - una spesa eliminata non ricorda **chi** l'ha eliminata: c'è `deleted_at`,
 *   non `deleted_by`;
 * - l'ingresso di una persona nel gruppo non lascia nessuna riga in nessuna
 *   delle due tabelle.
 *
 * Quindi gli eventi si scrivono quando accadono, dentro la stessa transazione
 * dell'azione che li produce: non esiste una spesa senza il suo evento, né un
 * evento senza la sua spesa.
 */
export const ACTIVITY_KINDS = [
  "group_created",
  "member_added",
  "member_joined",
  "member_removed",
  "friend_added",
  "expense_created",
  "expense_updated",
  "expense_deleted",
  "settlement_created",
  "settlement_deleted",
] as const;

export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

export const activityKindSchema = z.enum(ACTIVITY_KINDS);

export const activityEventResponseSchema = z.object({
  id: z.string(),
  groupId: z.string(),
  /** Denormalizzato: il feed è globale e mostra il gruppo su ogni riga. */
  groupName: z.string(),
  kind: activityKindSchema,
  /** Chi ha fatto la cosa, come si chiamava **allora**. */
  actorName: z.string(),
  occurredAt: z.iso.datetime(),

  /** La spesa, il pareggio o la persona coinvolta. `null` per il gruppo. */
  subjectId: z.string().nullable(),
  /**
   * Come si chiamava il soggetto al momento del fatto.
   *
   * È una copia, non una lettura: rileggere la spesa mostrerebbe la
   * descrizione **nuova** accanto a «ha modificato», che è esattamente
   * l'informazione sbagliata, e non mostrerebbe niente per una spesa cancellata
   * davvero.
   */
  subjectLabel: z.string().nullable(),
  /** L'altra persona, quando ce n'è una: chi riceve un pagamento. */
  counterpartLabel: z.string().nullable(),

  amountCents: z.number().int().nullable(),
  currency: z.enum(CURRENCIES).nullable(),
});

export type ActivityEventResponse = z.infer<typeof activityEventResponseSchema>;

/**
 * Le famiglie di attività, per filtrare.
 *
 * Quattro e non dieci: rispondono a domande diverse — cosa abbiamo comprato,
 * chi ha saldato, chi è arrivato — mentre distinguere «spesa aggiunta» da
 * «spesa modificata» separa sfumature dello stesso fatto, che nessuno cerca
 * una senza l'altra. Dieci pastiglie su un telefono non si scorrono.
 */
export const ACTIVITY_CATEGORIES = ["expenses", "settlements", "people", "groups"] as const;

export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];

export const activityCategorySchema = z.enum(ACTIVITY_CATEGORIES);

/**
 * Quali tipi finiscono in quale famiglia.
 *
 * **Ogni tipo deve stare in esattamente una famiglia**, ed è verificato da un
 * test: un tipo dimenticato qui sparirebbe da tutti i filtri e ricomparirebbe
 * solo su «Tutto» — un buco che nessuno andrebbe a cercare.
 */
export const ACTIVITY_CATEGORY_KINDS: Record<ActivityCategory, readonly ActivityKind[]> = {
  expenses: ["expense_created", "expense_updated", "expense_deleted"],
  settlements: ["settlement_created", "settlement_deleted"],
  people: ["member_added", "member_joined", "member_removed", "friend_added"],
  groups: ["group_created"],
};

export const ACTIVITY_CATEGORY_LABELS: Record<ActivityCategory, string> = {
  expenses: "Spese",
  settlements: "Pagamenti",
  people: "Persone",
  groups: "Gruppi",
};

/**
 * La famiglia di un tipo.
 *
 * Il contrario di `ACTIVITY_CATEGORY_KINDS`, ed è totale: un test verifica che
 * ogni tipo stia in esattamente una famiglia, quindi questa non torna mai
 * `undefined`.
 */
export function categoryOfKind(kind: ActivityKind): ActivityCategory {
  const found = ACTIVITY_CATEGORIES.find((category) =>
    ACTIVITY_CATEGORY_KINDS[category].includes(kind),
  );

  return found ?? "groups";
}

/** I tipi coperti da una selezione. Vuota significa tutti. */
export function kindsForCategories(
  categories: readonly ActivityCategory[],
): readonly ActivityKind[] {
  if (categories.length === 0) return ACTIVITY_KINDS;

  return categories.flatMap((category) => ACTIVITY_CATEGORY_KINDS[category]);
}

export const activityFeedResponseSchema = z.object({
  events: z.array(activityEventResponseSchema),
  /** `occurredAt` da passare come `before` per la pagina successiva. */
  nextBefore: z.iso.datetime().nullable(),
});

export type ActivityFeedResponse = z.infer<typeof activityFeedResponseSchema>;

/** Quante voci per pagina, se non si chiede altro. */
export const ACTIVITY_PAGE_SIZE = 30;
export const ACTIVITY_MAX_PAGE_SIZE = 100;
